import { OnboardingEventId } from '@thoughtspot/onboarding-event-service';
import {
    TourChainActionType,
    TourChainStep,
} from './checklist-tour-steps.util';

export const getCreateInsightsTourChain = (
    taskId: string,
    t: (key: string, params?: Record<string, unknown>) => string,
): TourChainStep[] | null => {
    switch (taskId) {
        case OnboardingEventId.CREATE_INSIGHTS_CUSTOMIZE_DASHBOARD:
            return [
                {
                    steps: [
                        {
                            target:
                                '[data-tour-id="tour_header-title-container"]',
                            title: t(
                                'sparkflow.customize_dashboard.step1.title',
                            ),
                            content: t(
                                'sparkflow.customize_dashboard.step1.body',
                            ),
                            disableBeacon: true,
                            placement: 'right',
                        },
                        {
                            target: '[data-tour-id="tour_header-menu-button"]',
                            title: t(
                                'sparkflow.customize_dashboard.step2.title',
                            ),
                            content: t(
                                'sparkflow.customize_dashboard.step2.body',
                            ),
                            disableBeacon: true,
                            placement: 'left',
                        },
                    ],
                    afterAction: {
                        selector: '[data-tour-id="tour_header-menu-button"]',
                        type: TourChainActionType.CLICK,
                    },
                },
                {
                    steps: [
                        {
                            target: '[data-tour-id="tour_answer-content"]',
                            title: t(
                                'sparkflow.customize_dashboard.step3.title',
                            ),
                            content: t(
                                'sparkflow.customize_dashboard.step3.body',
                            ),
                            disableBeacon: true,
                            placement: 'bottom',
                        },
                    ],
                    waitForTargetRemoval: true,
                },
            ];

        case OnboardingEventId.CREATE_INSIGHTS_REFINE_CHART:
            return [
                {
                    steps: [
                        {
                            target:
                                '[data-tour-id="tour_viz-header-menu-button"]',
                            title: t(
                                'sparkflow.refine_individual_charts.step1.title',
                            ),
                            content: t(
                                'sparkflow.refine_individual_charts.step1.body',
                            ),
                            disableBeacon: true,
                            placement: 'bottom',
                        },
                    ],
                    beforeAction: {
                        selector:
                            '[data-tour-id="tour_viz-header-menu-button"]',
                        type: TourChainActionType.HOVER,
                    },
                },
                {
                    steps: [
                        {
                            target:
                                '[data-tour-id="tour_left-data-panel-sources-filter-input"]',
                            title: t(
                                'sparkflow.refine_individual_charts.step2.title',
                            ),
                            content: t(
                                'sparkflow.refine_individual_charts.step2.body',
                            ),
                            disableBeacon: true,
                            placement: 'right',
                        },
                        {
                            target:
                                '[data-tour-id="tour_menu-icon-CHART_TYPE"]',
                            title: t(
                                'sparkflow.refine_individual_charts.step3.title',
                            ),
                            content: t(
                                'sparkflow.refine_individual_charts.step3.body',
                            ),
                            disableBeacon: true,
                            placement: 'left',
                        },
                    ],
                    beforeActions: [
                        {
                            selector:
                                '[data-tour-id="tour_viz-header-menu-button"]',
                            type: TourChainActionType.CLICK,
                        },
                        {
                            selector: '[data-testid="edit"]',
                            type: TourChainActionType.CLICK,
                        },
                    ],
                },
            ];

        case OnboardingEventId.CREATE_INSIGHTS_BUILD_VISUALIZATION:
            return [
                {
                    steps: [
                        {
                            target:
                                '[data-tour-id="tour_create-new-insights-button"]',
                            title: t(
                                'sparkflow.build_your_own_insights.step1.title',
                            ),
                            content: t(
                                'sparkflow.build_your_own_insights.step1.body',
                            ),
                            disableBeacon: true,
                            placement: 'right',
                        },
                    ],
                    nextUrl: '/insights/answer',
                },
                {
                    steps: [
                        {
                            target: '[data-tour-id="tour_sage-search-box"]',
                            title: t(
                                'sparkflow.build_your_own_insights.step2.title',
                            ),
                            content: t(
                                'sparkflow.build_your_own_insights.step2.body',
                            ),
                            disableBeacon: true,
                            placement: 'bottom',
                        },
                    ],
                },
                {
                    steps: [
                        {
                            target:
                                '[data-tour-id="tour_pin-dual-intent-one-click-pin"]',
                            title: t(
                                'sparkflow.build_your_own_insights.step3.title',
                            ),
                            content: t(
                                'sparkflow.build_your_own_insights.step3.body',
                            ),
                            disableBeacon: true,
                            placement: 'bottom',
                        },
                    ],
                },
                {
                    steps: [
                        {
                            target:
                                '[data-tour-id="tour_pinboard_header_info_button"]',
                            title: t(
                                'sparkflow.build_your_own_insights.step4.title',
                            ),
                            content: t(
                                'sparkflow.build_your_own_insights.step4.body',
                            ),
                            disableBeacon: true,
                            placement: 'bottom',
                        },
                    ],
                },
            ];

        case OnboardingEventId.CREATE_INSIGHTS_CONNECT_DATA:
            return [
                {
                    condition: () =>
                        !window.location.href.includes(
                            'type=RDBMS_GOOGLE_SHEETS',
                        ),
                    steps: [
                        {
                            target:
                                '[data-tour-id="tour_connection-wizard-table-selection"]',
                            title: t(
                                'sparkflow.connect_your_own_data.step1_data_connection.title',
                            ),
                            content: t(
                                'sparkflow.connect_your_own_data.step1_data_connection.body',
                            ),
                            disableBeacon: true,
                            placement: 'right',
                        },
                    ],
                },
                {
                    condition: () =>
                        window.location.href.includes(
                            'type=RDBMS_GOOGLE_SHEETS',
                        ),
                    steps: [
                        {
                            target:
                                '[data-tour-id="tour_connection-wizard-table-selection"]',
                            title: t(
                                'sparkflow.connect_your_own_data.step1_google_sheets.title',
                            ),
                            content: t(
                                'sparkflow.connect_your_own_data.step1_google_sheets.body',
                            ),
                            disableBeacon: true,
                            placement: 'right',
                        },
                    ],
                },
                {
                    steps: [
                        {
                            target: '[data-tour-id="tour_schema-graph"]',
                            title: t(
                                'sparkflow.connect_your_own_data.step3.titile',
                            ),
                            content: t(
                                'sparkflow.connect_your_own_data.step3.titile',
                            ),
                            disableBeacon: true,
                            placement: 'top',
                        },
                    ],
                },
                {
                    steps: [
                        {
                            target:
                                '[data-tour-id="tour_left-panel-search-result-container"]',
                            title: t(
                                'sparkflow.connect_your_own_data.step4.titile',
                            ),
                            content: t(
                                'sparkflow.connect_your_own_data.step4.titile',
                            ),
                            disableBeacon: true,
                            placement: 'right',
                        },
                    ],
                },
            ];

        case OnboardingEventId.CREATE_INSIGHTS_TRAIN_AI:
            return [
                {
                    steps: [
                        {
                            target:
                                '[data-testid="object-menu-options-button"]',
                            title: t('sparkflow.train_spotter.step1.title'),
                            content: t('sparkflow.train_spotter.step1.body'),
                            disableBeacon: true,
                            placement: 'left',
                        },
                        {
                            target: '[data-testid="tab-header-item-10"]',
                            title: t('sparkflow.train_spotter.step2.title'),
                            content: t('sparkflow.train_spotter.step2.body'),
                            disableBeacon: true,
                            placement: 'bottom',
                        },
                        {
                            target: '[id="memory"]',
                            title: t('sparkflow.train_spotter.step3.title'),
                            content: t('sparkflow.train_spotter.step3.body'),
                            disableBeacon: true,
                            placement: 'right',
                        },
                    ],
                    onCompleteEventId:
                        OnboardingEventId.CREATE_INSIGHTS_TRAIN_AI,
                },
            ];

        default:
            return null;
    }
};
